
import { computed, ref, watch, defineComponent, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useNSProtectedStore } from '@/store';
import SkeletonLoader from '@/components/utils/ux/SkeletonLoader.vue';
import TooltipCustom from '@/components/TooltipCustom.vue';
import { STSLANDINGGETC360ACCTS } from '@/store/sagas/patterns';
import * as tkGCAP from '@/store/modules/getC360AcctsParallelModule/treekeys';
import * as tkSTSL from '@/store/modules/stsLandingModule/treekeys';
import * as tkA from '@/store/modules/auth/treekeys';
import { useVuelidateRulesSingleton } from '@/common/utils/vuelidateRulesSingleton';
import { ROUTERNAME_STSPOSTLOGIN } from '@/common/strings';
import * as envVars from '@/common/envVars';
import { useNSProtectedSagaDispatch } from '@/store/protectedVuexSaga';
import {
  C360AccountsBusinessResponse,
  GetC360AcctsState,
} from '@/store/modules/getC360AcctsParallelModule/types';
import { useGtmUtilsSingleton } from '@/common/utils/gtmUtilsSingleton';
import { useErrorUtilsSingleton } from '@/common/utils/errorUtilsSingleton';

export default defineComponent({
  name: 'STSLandingBody',
  components: {
    SkeletonLoader,
    TooltipCustom,
  },
  props: {
    updateValidation: {
      required: false,
      type: Function,
      default: null,
    },
    urlQuery: {
      required: false,
      type: String,
      default: '',
    },
    getRXProductInfoState: {
      required: false,
      type: Object,
      default: null,
    },
  },
  setup(props: any) {
    const router = useRouter();
    const store = useNSProtectedStore(tkSTSL.STSLANDINGMODULE);
    const c360 = useNSProtectedStore(tkGCAP.GETC360ACCTSPARALLELMODULE);
    const sagaDispatch = useNSProtectedSagaDispatch(tkSTSL.STSLANDINGMODULE);
    const vuelidateRules = useVuelidateRulesSingleton();
    const errorUtils = useErrorUtilsSingleton();

    const billingZip = ref(
      store.getters[tkSTSL.NS_GET_STSLANDINGMODULE_SELECTEDZIPCODE]
    );
    const showNext = ref(true);
    const getC360AcctsState = ref<GetC360AcctsState>();
    const c360AcctList = ref<C360AccountsBusinessResponse>();
    const c360AcctListErr = ref();
    const gtm = useGtmUtilsSingleton();
    const isVisibleOnMobile = ref(false);
    const isVisibleZipOnMobile = ref(false);
    const isMounted = ref(false);

    const showCustomTooltip = () => {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      ) {
        isVisibleOnMobile.value = !isVisibleOnMobile.value;
      }
    };

    const showCustomZipTooltip = () => {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      ) {
        isVisibleZipOnMobile.value = !isVisibleZipOnMobile.value;
      }
    };

    const numberQuery = ref(
      store.getters[tkSTSL.NS_GET_STSLANDINGMODULE_SELECTEDACCOUNTNUMBER]
    );

    const validateRules = {
      numberQuery: {
        required,
        isNotNoAcctsFoundErr: () =>
          !vuelidateRules.isNoAcctsFoundError(c360AcctList.value || {}),
        isNotAcctDeactivatedErr: () =>
          !vuelidateRules.isAcctDeactivatedError(c360AcctList.value || {}),
        isRegexMatch: (num: any) => vuelidateRules.isSAMAcctNumRegexMatch(num),
        // Next Rules will trigger for URL queries only..
        isAcctNumQueryRegexMatch: (acctNum: any) =>
          !props.urlQuery ||
          !props.urlQuery.includes('accountNumber') ||
          vuelidateRules.isSAMAcctNumBasicRegexMatch(acctNum),
        isEmailQueryRegexMatch: (email: any) =>
          !props.urlQuery ||
          !props.urlQuery.includes('email') ||
          vuelidateRules.isEmailBasicRegexMatch(email),
        isPhoneNumQueryRegexMatch: (phone: any) =>
          !props.urlQuery ||
          !props.urlQuery.includes('phoneNumber') ||
          vuelidateRules.isPhoneNumberBasicRegexMatch(phone),
      },
      c360AcctList: {
        isNotNoAcctsFoundErr: (resp: any) =>
          !vuelidateRules.isNoAcctsFoundError(resp),
        isNotAcctDeactivatedErr: (resp: any) =>
          !vuelidateRules.isAcctDeactivatedError(resp),
        hasAcctsWithoutBadStatusesWithZip: (resp: any) =>
          vuelidateRules.hasAcctsWithoutBadStatuses(resp),
      },
      billingZip: {
        required,
        isZipCodeRegexMatch: (zip: any) =>
          vuelidateRules.isZipCodeRegexMatch(zip),
        hasAcctsWithoutBadStatusesWithZip: (zip: any) =>
          vuelidateRules.hasAcctsWithoutBadStatusesWithZip(
            c360AcctList.value || {},
            zip
          ),
      },
    };

    const removeSpecialCharacters = (charactersString: string) =>
      charactersString.replace('-', '').replace(/\s/g, '');

    watch(
      () => numberQuery.value,

      (newVal: any) => {
        numberQuery.value = removeSpecialCharacters(newVal);
      }
    );
    const selectedIdentifier = ref('accountNumber');

    const v$ = useVuelidate(validateRules, {
      numberQuery,
      c360AcctList,
      billingZip,
    });

    const handleStartSubmit = (valid: boolean) => {
      if (valid) {
        gtm.trackGTMEvent({
          event: 'sts_start',
          accountNumber: gtm.pii(numberQuery.value),
          zip: gtm.pii(billingZip.value),
          flow: {
            name: 'sts',
            step: 'ctl|rsd|support|repair|sts|enter_credentials|button|start',
          },
        });
      } else {
        gtm.trackGTMEvent({
          event: 'sts_error',
          accountNumber: '',
          zip: '',
          flow: {
            name: 'sts_error',
            step: 'ctl|rsd|support|repair|sts|enter_credentials|button|start|invalid_credentials',
          },
        });
      }

      store.dispatch(
        tkSTSL.NS_ACTION_STSLANDINGMODULE_SHOWVALIDATEERRORSACCOUNTNUMBER,
        {
          showValidateErrorsAccountNumber: true,
        }
      );

      if (!valid) {
        return;
      }

      sagaDispatch(STSLANDINGGETC360ACCTS, {
        selectedAccountNumber:
          store.getters[tkSTSL.NS_GET_STSLANDINGMODULE_SELECTEDACCOUNTNUMBER],
        selectedZipCode:
          store.getters[tkSTSL.NS_GET_STSLANDINGMODULE_SELECTEDZIPCODE],
      });
    };

    const redirectToLogin = () => {
      gtm.trackGTMEvent({
        event: 'sts_login_click',
        flow: {
          name: 'sts_login_click',
          step: 'ctl|rsd|support|repair|sts|enter_credentials|button|login',
        },
      });
      router.push({
        name: ROUTERNAME_STSPOSTLOGIN,
      });
    };

    watch(
      [isMounted, () => store.getters[tkA.NS_GET_AUTH_AUTHENTICATED]],
      ([newIsMounted, newIsAuthenticated]) => {
        if (newIsMounted && newIsAuthenticated === true) {
          const rxProductInfoErr =
            props.getRXProductInfoState?.rxProductInfoError;
          if (
            rxProductInfoErr &&
            errorUtils.isRxProductInfoError(rxProductInfoErr)
          ) {
            return;
          }
          router.push({
            name: ROUTERNAME_STSPOSTLOGIN,
          });
        }
        // } else if (props.urlQuery) {
        //   handleStartSubmit(!v$.value.$invalid);
        // }
      }
    );

    onMounted(() => {
      isMounted.value = true;
    });

    watch(
      () => getC360AcctsState.value?.c360AccountList,
      (newVal: any) => {
        c360AcctList.value = newVal;
      }
    );
    watch(
      () => getC360AcctsState.value?.c360AccountListError,
      (newVal: any) => {
        c360AcctListErr.value = newVal;
      }
    );
    watch(
      () =>
        store.getters[
          tkGCAP.NS_GET_GETC360ACCTSPARALLELMODULE_GETC360ACCTSPARALLELMAP
        ]?.[numberQuery.value],
      (newVal: any) => {
        getC360AcctsState.value = newVal;
      },
      { immediate: true }
    );

    watch(
      () => store.getters[tkSTSL.NS_GET_STSLANDINGMODULE_THROTTLED],
      (newVal: any, oldVal: any) => {
        if (newVal && !oldVal) {
          showNext.value = false;
        }
      },
      { immediate: true }
    );

    watch(numberQuery, (newVal: any) => {
      store.dispatch(tkSTSL.NS_ACTION_STSLANDINGMODULE_SELECTEDACCOUNTNUMBER, {
        selectedAccountNumber: newVal,
      });
      showNext.value = true;
    });

    watch(billingZip, (newVal: any) => {
      store.dispatch(tkSTSL.NS_ACTION_STSLANDINGMODULE_SELECTEDZIPCODE, {
        selectedZipCode: newVal,
      });
      c360AcctList.value = {};
      if (
        !v$.value.billingZip.isZipCodeRegexMatch.$invalid &&
        c360AcctList.value?.c360AccountList?.accounts?.length === 0
      ) {
        c360.dispatch(
          tkGCAP.NS_ACTION_GETC360ACCTSPARALLELMODULE_GETC360ACCTSPARALLELMAP,
          {
            getC360AcctsParallelMap: {},
          }
        );
      }

      showNext.value = true;
    });

    watch(
      () => v$.value.$invalid,
      () => {
        if (props.updateValidation) {
          props.updateValidation(!v$.value.$invalid);
        }
      },
      { immediate: true }
    );

    return {
      v$,
      showCustomTooltip,
      isVisibleOnMobile,
      showCustomZipTooltip,
      isVisibleZipOnMobile,
      selectedIdentifier,
      numberQuery,
      billingZip,
      showNext,
      handleStartSubmit,
      dhHostEnvVar: envVars.VUE_APP_DH_HOST,
      redirectToLogin,
      pending: computed(() => getC360AcctsState.value?.pending),
      throttled: computed(
        () => store.getters[tkSTSL.NS_GET_STSLANDINGMODULE_THROTTLED]
      ),
      areInputsEmpty: computed(
        () =>
          !numberQuery.value ||
          numberQuery.value.length <= 0 ||
          !billingZip.value ||
          billingZip.value.length <= 0
      ),
      c360AcctList,
      showValidateErrorsAccountNumber: computed(
        () =>
          store.getters[
            tkSTSL.NS_GET_STSLANDINGMODULE_SHOWVALIDATEERRORSACCOUNTNUMBER
          ]
      ),
    };
  },
});
